import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Table, Button, Modal, Form } from 'react-bootstrap';
import AddNewButton from '../../../Common/AddNewButton';
import { uploadAgeCategoryImage, addAgeCategory, editAgeCategory, deleteAgeCategory, getAgeCategoryById } from '../../../api/tournament/ageCategoryService';
import '../../BannerManagement/components/BannerList.css';
import '../../WeeklyReports/components/WeeklyReportsTable.css';
import './TournamentList.css';
import '../../../Common/ManageTournamentsFixturesTable.css';
import { DeleteModal } from '../../../Common/DeleteModal';
import Select from 'react-select';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { getImageUrl } from '../../../utils/imageUrl';
import { TOURNAMENT_KNOCKOUT_FORMATS } from '../../../api/tournament/types';

interface AgeCategory {
  _id: string;
  ageCategory: string;
  standingsType: string;
  format: string;
  knockoutFormat?: string;
  liveImage: string;
  highlightsImage: string;
  tournament: {
    _id: string;
    name: string;
    shortName: string;
  };
}

interface AgeCategoryTableProps {
  ageCategories: AgeCategory[];
  search: string;
  onSearch: (value: string) => void;
  onAdd: () => void;
  onEdit: () => void;
  onDelete: () => void;
  tournamentId: string;
  ageCategoriesLoading: boolean;
  page: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  typeFilter: any;
  setTypeFilter: (option: any) => void;
  formatFilter: any;
  setFormatFilter: (option: any) => void;
  typeOptions: any[];
  formatOptions: any[];
  tournamentSports?: string; // Add sport prop for API calls
}

// Define types for react-select options
interface OptionType { value: string; label: string; }

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const AgeCategoryTable: React.FC<AgeCategoryTableProps> = React.memo(({
  ageCategories, search, onSearch, onAdd, onEdit, onDelete, tournamentId, ageCategoriesLoading, page, pageSize, totalPages, totalCount, onPageChange, onPageSizeChange, typeFilter, setTypeFilter, formatFilter, setFormatFilter, typeOptions, formatOptions, tournamentSports
}) => {
  // Helper to get sport - use prop if provided, otherwise throw error
  const getSport = (): string => {
    if (tournamentSports) {
      return tournamentSports;
    }
    throw new Error('Tournament sport is required. Please ensure tournamentSports prop is passed to AgeCategoryTable.');
  };
  
  const [showModal, setShowModal] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [liveImageUploading, setLiveImageUploading] = useState(false);
  const [highlightsImageUploading, setHighlightsImageUploading] = useState(false);
  const [liveImageError, setLiveImageError] = useState<string | null>(null);
  const [highlightsImageError, setHighlightsImageError] = useState<string | null>(null);
  const [form, setForm] = useState<{
    ageCategory: string;
    standingsType: OptionType | null;
    format: OptionType | null;
    knockoutFormat: OptionType | null;
    liveImage: string;
    highlightsImage: string;
    tournament: {
      _id: string;
      name: string;
      shortName: string;
    };
  }>({
    ageCategory: '',
    standingsType: null,
    format: null,
    knockoutFormat: null,
    liveImage: '',
    highlightsImage: '',
    tournament: {
      _id: '',
      name: '',
      shortName: ''
    }
  });
  const [formTouched, setFormTouched] = useState<any>({});
  const [fieldErrors, setFieldErrors] = useState<any>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingAgeCategory, setLoadingAgeCategory] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [originalEditValues, setOriginalEditValues] = useState<any>(null);

  // Debounced search input state
  const [inputValue, setInputValue] = useState(search);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep input focused and cursor at end after parent re-renders
  useEffect(() => {
    if (search === '' && inputValue !== '') {
      setInputValue('');
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        }
      }, 0);
    }
  }, [search]);

  // Always keep focus after rerender if input is focused
  useEffect(() => {
    if (document.activeElement === inputRef.current) {
      inputRef.current?.focus();
    }
  });

  // Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputValue.length === 0 || inputValue.length >= 2) {
        onSearch(inputValue);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [inputValue, onSearch]);

  // Filter ageCategories by standingsType and format
  const filteredAgeCategories = ageCategories.filter(ac => {
    const matchesType = !typeFilter || (ac.standingsType && ac.standingsType.toLowerCase() === typeFilter.value.toLowerCase());
    const matchesFormat = !formatFilter || (ac.format && ac.format.toLowerCase() === formatFilter.value.toLowerCase());
    return matchesType && matchesFormat;
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormTouched({ ...formTouched, [e.target.name]: true });
  };
  const handleStandingsTypeChange = (option: OptionType | null) => {
    setForm({ ...form, standingsType: option });
    setFormTouched({ ...formTouched, standingsType: true });
  };
  const handleFormatChange = (option: OptionType | null) => {
    setForm({ ...form, format: option });
    setFormTouched({ ...formTouched, format: true });
  };
  const handleKnockoutFormatChange = (option: OptionType | null) => {
    setForm({ ...form, knockoutFormat: option });
    setFormTouched({ ...formTouched, knockoutFormat: true });
  };
  const handleLiveImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLiveImageUploading(true);
    setLiveImageError(null);
    try {
      const s3Key = await uploadAgeCategoryImage(file, 'liveImage');
      setForm({ ...form, liveImage: s3Key });
      setFormTouched({ ...formTouched, liveImage: true });
      console.log('liveImage S3 key set:', s3Key);
    } catch (err) {
      setLiveImageError('Failed to upload live image.');
    } finally {
      setLiveImageUploading(false);
    }
  };
  const handleHighlightsImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHighlightsImageUploading(true);
    setHighlightsImageError(null);
    try {
      const s3Key = await uploadAgeCategoryImage(file, 'highlightsImage');
      setForm({ ...form, highlightsImage: s3Key });
      setFormTouched({ ...formTouched, highlightsImage: true });
      console.log('highlightsImage S3 key set:', s3Key);
    } catch (err) {
      setHighlightsImageError('Failed to upload highlights image.');
    } finally {
      setHighlightsImageUploading(false);
    }
  };

  const handleAdd = () => {
    setEditIndex(null);
    setForm({ 
      ageCategory: '', 
      standingsType: null, 
      format: null,
      knockoutFormat: null,
      liveImage: '', 
      highlightsImage: '',
      tournament: {
        _id: tournamentId,
        name: '',
        shortName: ''
      }
    });
    setFormTouched({});
    setFieldErrors({});
    setShowModal(true);
  };
  const handleEdit = async (idx: number) => {
    const ac = ageCategories[idx];
    if (!ac || !ac._id) {
      setFormError('Age category not found.');
      return;
    }

    setLoadingAgeCategory(true);
    setFormError(null);
    
    try {
      const sport = getSport();
      const response = await getAgeCategoryById(ac._id, sport);
      const ageCategoryData = response?.data || response;
      
      if (ageCategoryData) {
        setEditIndex(idx);
        
        // Find matching options with fallback
        const standingsTypeOption = typeOptions.find(opt => opt.value === ageCategoryData.standingsType) || 
          (ageCategoryData.standingsType ? { value: ageCategoryData.standingsType, label: ageCategoryData.standingsType.charAt(0).toUpperCase() + ageCategoryData.standingsType.slice(1) } : null);
        
        const formatOption = formatOptions.find(opt => opt.value === ageCategoryData.format) || 
          (ageCategoryData.format ? { value: ageCategoryData.format, label: ageCategoryData.format.charAt(0).toUpperCase() + ageCategoryData.format.slice(1) } : null);
        
        const knockoutFormatOptions = [
          { value: TOURNAMENT_KNOCKOUT_FORMATS.KNOCKOUT_WITH_BYES, label: 'Knockout with Byes' },
          { value: TOURNAMENT_KNOCKOUT_FORMATS.SINGLE_ELIMINATION, label: 'Single Elimination' }
        ];
        
        const knockoutFormatOption = knockoutFormatOptions.find(opt => opt.value === ageCategoryData.knockoutFormat) || 
          (ageCategoryData.knockoutFormat ? { value: ageCategoryData.knockoutFormat, label: ageCategoryData.knockoutFormat.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) } : null);
        
        const editForm = {
          ageCategory: ageCategoryData.ageCategory || '',
          standingsType: standingsTypeOption,
          format: formatOption,
          knockoutFormat: knockoutFormatOption,
          liveImage: ageCategoryData.liveImage || '',
          highlightsImage: ageCategoryData.highlightsImage || '',
          tournament: ageCategoryData.tournament || ac.tournament || {
            _id: tournamentId,
            name: '',
            shortName: ''
          }
        };
        setForm(editForm);
        setOriginalEditValues(editForm);
        setFormTouched({});
        setFieldErrors({});
        setShowModal(true);
        
        // Scroll to top for better user experience
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setFormError('Age category not found.');
      }
    } catch (err: any) {
      console.error('Error loading age category:', err);
      let errorMessage = 'Failed to load age category. Please try again.';
      
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setFormError(errorMessage);
    } finally {
      setLoadingAgeCategory(false);
    }
  };
  const handleClose = () => {
    setShowModal(false);
    setForm({ 
      ageCategory: '', 
      standingsType: null, 
      format: null,
      knockoutFormat: null,
      liveImage: '', 
      highlightsImage: '',
      tournament: {
        _id: '',
        name: '',
        shortName: ''
      }
    });
    setFormTouched({});
    setFieldErrors({});
  };

  const handleView = (idx: number) => {
    const ac = ageCategories[idx];
    // For now, we'll just show an alert with the age category details
    // In the future, this could open a detailed view modal
    alert(`Age Category: ${ac.ageCategory}\nFormat: ${ac.format}\nStandings Type: ${ac.standingsType}\nTournament: ${ac.tournament?.name || 'N/A'}`);
  };

  const handleDelete = (idx: number) => {
    setDeleteIndex(idx);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (deleteIndex !== null) {
      const deleteId = ageCategories[deleteIndex]._id;
      try {
        // Get sport from prop or default to 'Football'
        const sport = getSport();
        // FIX: Pass sport parameter to deleteAgeCategory
        await deleteAgeCategory(deleteId, sport);
        setShowDeleteModal(false);
        setDeleteIndex(null);
        console.log('Calling onDelete refresh...');
        onDelete(); // refresh list
      } catch (err) {
        setFormError('Failed to delete age category.');
      }
    }
  };
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteIndex(null);
  };

  const validateForm = () => {
    return {
      ageCategory: !form.ageCategory.trim() ? 'Age Category is required.' : '',
      standingsType: !form.standingsType ? 'Standings Type is required.' : '',
      format: !form.format ? 'Format is required.' : '',
      liveImage: '',
      highlightsImage: !form.highlightsImage ? 'Highlights Image is required.' : '',
    };
  };
  const errors = validateForm();
  const isFormValid = Object.values(errors).every(x => !x);

  const handleSave = async () => {
    setFormTouched({
      ageCategory: true,
      standingsType: true,
      format: true,
      liveImage: true,
      highlightsImage: true,
    });
    setFieldErrors({});
    if (!isFormValid) {
      return;
    }
    setFormError(null);
    setSaving(true);
    try {
      // Get sport from prop - throw error if missing
      const sport = getSport();
      
      if (editIndex === null) {
        // FIX: Pass sport parameter to addAgeCategory
        await addAgeCategory({
          ageCategory: form.ageCategory,
          standingsType: form.standingsType ? form.standingsType.value : '',
          format: form.format ? form.format.value : '',
          knockoutFormat: form.knockoutFormat ? form.knockoutFormat.value : undefined,
          liveImage: form.liveImage,
          highlightsImage: form.highlightsImage,
          tournament: tournamentId,
        }, sport);
      } else {
        const acId = ageCategories[editIndex]._id;
        // Only send changed fields
        const changed: any = {};
        if (form.ageCategory !== originalEditValues.ageCategory) changed.ageCategory = form.ageCategory;
        if ((form.standingsType && form.standingsType.value) !== (originalEditValues.standingsType && originalEditValues.standingsType.value)) changed.standingsType = form.standingsType ? form.standingsType.value : '';
        if ((form.format && form.format.value) !== (originalEditValues.format && originalEditValues.format.value)) changed.format = form.format ? form.format.value : '';
        if ((form.knockoutFormat && form.knockoutFormat.value) !== (originalEditValues.knockoutFormat && originalEditValues.knockoutFormat?.value)) changed.knockoutFormat = form.knockoutFormat ? form.knockoutFormat.value : undefined;
        if (form.liveImage !== originalEditValues.liveImage) changed.liveImage = form.liveImage;
        if (form.highlightsImage !== originalEditValues.highlightsImage) changed.highlightsImage = form.highlightsImage;
        changed.tournament = tournamentId;
        // FIX: Pass sport parameter to editAgeCategory
        await editAgeCategory(acId, changed, sport);
      }
      setShowModal(false);
      // Trigger refresh after successful save
      if (editIndex === null) {
        console.log('Calling onAdd refresh...');
        onAdd();
      } else {
        console.log('Calling onEdit refresh...');
        onEdit();
      }
    } catch (err: any) {
      // If backend returns field errors, set them under the correct field
      if (err.response && err.response.data && err.response.data.errors) {
        setFieldErrors(err.response.data.errors);
      } else if (err.response && err.response.data && err.response.data.message) {
        // Try to assign to a field if possible, else fallback to formError
        const msg = err.response.data.message;
        if (msg.toLowerCase().includes('age category')) setFieldErrors((prev: any) => ({ ...prev, ageCategory: msg }));
        else if (msg.toLowerCase().includes('standings') || msg.toLowerCase().includes('type')) setFieldErrors((prev: any) => ({ ...prev, standingsType: msg }));
        else if (msg.toLowerCase().includes('format')) setFieldErrors((prev: any) => ({ ...prev, format: msg }));
        else if (msg.toLowerCase().includes('live')) setFieldErrors((prev: any) => ({ ...prev, liveImage: msg }));
        else if (msg.toLowerCase().includes('highlight')) setFieldErrors((prev: any) => ({ ...prev, highlightsImage: msg }));
        else setFormError(msg);
      } else {
        setFormError('Failed to save age category.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-3" style={{ flex: 1 }}>
            <div style={{ position: 'relative', width: 260 }}>
              <input
                type="text"
                className="form-control form-search"
                style={{ minWidth: 220, maxWidth: 260, height: 40, borderRadius: 8, paddingRight: 36 }}
                placeholder="Search age categories..."
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                ref={inputRef}
                autoFocus
              />
              {inputValue && (
                <span
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#aaa',
                    fontSize: 20,
                    zIndex: 2
                  }}
                  onClick={() => { setInputValue(''); onSearch(''); }}
                  title="Clear"
                >
                  <i className="ri-close-fill"></i>
                </span>
              )}
            </div>
            <div style={{ minWidth: 180 }}>
              <Select
                classNamePrefix="react-select"
                name="typeFilter"
                value={typeFilter}
                onChange={setTypeFilter}
                options={typeOptions}
                placeholder="Type"
                isClearable
                isSearchable
                styles={{ control: base => ({ ...base, minHeight: '40px', borderRadius: 8, fontSize: 14 }) }}
              />
            </div>
            <div style={{ minWidth: 180 }}>
              <Select
                classNamePrefix="react-select"
                name="formatFilter"
                value={formatFilter}
                onChange={setFormatFilter}
                options={formatOptions}
                placeholder="Format"
                isClearable
                isSearchable
                styles={{ control: base => ({ ...base, minHeight: '40px', borderRadius: 8, fontSize: 14 }) }}
              />
            </div>
          </div>
          <AddNewButton onClick={handleAdd} label="Add New" />
        </div>
        <div className="manage-tournaments-table-container">
          <Table className="manage-tournaments-table table-hover">
            <thead>
              <tr>
                <th>Age Category</th>
                <th>Tournament</th>
                <th>Format</th>
                <th>Standings Type</th>
                <th>Normal Match Banner</th>
                <th>Highlights Image</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ageCategoriesLoading ? (
                <tr>
                  <td colSpan={7}><ShimmerLoader type="table" rows={5} /></td>
                </tr>
              ) : filteredAgeCategories.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center">No age categories found.</td>
                </tr>
              ) : (
                filteredAgeCategories.map((ac, idx) => (
                  <tr key={ac._id}>
                    <td>
                      <div className="fw-semibold">{ac.ageCategory}</div>
                    </td>
                    <td>
                      <div>
                        <div className="fw-medium">{ac.tournament?.name || '-'}</div>
                        {ac.tournament?.shortName && (
                          <div className="text-muted small">{ac.tournament.shortName}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-primary">
                        {formatOptions.find(opt => opt.value.toLowerCase() === (ac.format || '').toLowerCase())?.label || ac.format || '-'}
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-secondary">
                        {ac.standingsType || '-'}
                      </span>
                    </td>
                    <td>
                      {ac.liveImage ? (
                        <img 
                          src={getImageUrl(ac.liveImage)} 
                          alt="Live" 
                          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} 
                          onError={(e) => {
                            console.error('Table live image failed to load:', ac.liveImage);
                            console.error('Generated URL:', getImageUrl(ac.liveImage));
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log('Table live image loaded successfully:', ac.liveImage);
                          }}
                        />
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {ac.highlightsImage ? (
                        <img 
                          src={getImageUrl(ac.highlightsImage)} 
                          alt="Highlights" 
                          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} 
                          onError={(e) => {
                            console.error('Table highlights image failed to load:', ac.highlightsImage);
                            console.error('Generated URL:', getImageUrl(ac.highlightsImage));
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log('Table highlights image loaded successfully:', ac.highlightsImage);
                          }}
                        />
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <div className="tournament-actions">
                        <button
                          className="action-btn btn-view"
                          onClick={() => handleView(idx)}
                          title="View Age Category"
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                        <button
                          className="action-btn btn-edit"
                          onClick={() => handleEdit(idx)}
                          title="Edit Age Category"
                          disabled={loadingAgeCategory}
                        >
                          {loadingAgeCategory && editIndex === idx ? (
                            <i className="ri-loader-4-line ri-spin"></i>
                          ) : (
                            <i className="ri-pencil-line"></i>
                          )}
                        </button>
                        <button
                          className="action-btn btn-delete"
                          onClick={() => handleDelete(idx)}
                          title="Delete Age Category"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          {/* Pagination UI - WeeklyReports style */}
          {totalPages > 1 && (
            <div className="d-flex pagination-row align-items-center mt-3"
              style={{
                width: 1160,
                height: 52,
                justifyContent: 'space-between',
                borderRadius: '8px',
                paddingTop: 8,
                paddingRight: 12,
                paddingBottom: 8,
                paddingLeft: 12,
              }}
            >
              <div
                style={{
                  fontFamily: 'Helvetica Neue, Arial, sans-serif',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: 0,
                }}
              >
                Showing results
                <select
                  value={pageSize}
                  onChange={e => onPageSizeChange(Number(e.target.value))}
                  className="mx-2"
                  style={{
                    width: 70,
                    height: 36,
                    borderRadius: 8,
                    padding: 8,
                    gap: 8,
                    borderWidth: 0.5,
                    borderStyle: 'solid',
                    borderColor: '#888',
                    fontFamily: 'Helvetica Neue, Arial, sans-serif',
                    fontSize: '12px',
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                of {totalCount}
              </div>
              <div className="d-flex align-items-center">
                Page
                <button className="btn btn-light mx-1" onClick={() => onPageChange(page - 1)} disabled={page === 1}>&lt;</button>
                <span className="btn btn-primary mx-1 disabled">{page}</span>
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1 + page).map(p => (
                  p <= totalPages ? (
                    <button key={p} className="btn btn-light mx-1" onClick={() => onPageChange(p)}>{p}</button>
                  ) : null
                ))}
                {page < totalPages - 2 && <span className="mx-1">...</span>}
                <button className="btn btn-light mx-1" onClick={() => onPageChange(page + 1)} disabled={page === totalPages}>&gt;</button>
                <span className="mx-2">of {totalPages}</span>
              </div>
            </div>
          )}
        </div>
        <Modal show={showModal} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>{editIndex === null ? 'Add Age Category' : 'Edit Age Category'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <div className="alert alert-danger">{formError}</div>}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Age Category<span style={{ color: 'red' }}>*</span></Form.Label>
                <Form.Control name="ageCategory" value={form.ageCategory} onChange={handleInputChange} isInvalid={!!errors.ageCategory && formTouched.ageCategory} onBlur={() => setFormTouched({ ...formTouched, ageCategory: true })} />
                {(errors.ageCategory && formTouched.ageCategory) && <div className="text-danger">{errors.ageCategory}</div>}
                {fieldErrors.ageCategory && <div className="text-danger">{fieldErrors.ageCategory}</div>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Standings Type<span style={{ color: 'red' }}>*</span></Form.Label>
                <Select
                  classNamePrefix="react-select"
                  name="standingsType"
                  value={form.standingsType}
                  onChange={handleStandingsTypeChange}
                  options={typeOptions}
                  placeholder="Select Standings Type"
                  isClearable={false}
                  styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                />
                {(errors.standingsType && formTouched.standingsType) && <div className="text-danger">{errors.standingsType}</div>}
                {fieldErrors.standingsType && <div className="text-danger">{fieldErrors.standingsType}</div>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Format<span style={{ color: 'red' }}>*</span></Form.Label>
                <Select
                  classNamePrefix="react-select"
                  name="format"
                  value={form.format}
                  onChange={handleFormatChange}
                  options={formatOptions}
                  placeholder="Select Format"
                  isClearable={false}
                  styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                />
                {(errors.format && formTouched.format) && <div className="text-danger">{errors.format}</div>}
                {fieldErrors.format && <div className="text-danger">{fieldErrors.format}</div>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Knockout Format</Form.Label>
                <Select
                  classNamePrefix="react-select"
                  name="knockoutFormat"
                  value={form.knockoutFormat}
                  onChange={handleKnockoutFormatChange}
                  options={[
                    { value: TOURNAMENT_KNOCKOUT_FORMATS.KNOCKOUT_WITH_BYES, label: 'Knockout with Byes' },
                    { value: TOURNAMENT_KNOCKOUT_FORMATS.SINGLE_ELIMINATION, label: 'Single Elimination' }
                  ]}
                  placeholder="Select Knockout Format"
                  isClearable={true}
                  styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                />
                {fieldErrors.knockoutFormat && <div className="text-danger">{fieldErrors.knockoutFormat}</div>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Normal Match Banner</Form.Label>
                {form.liveImage ? (
                  <div>
                    <div className="mb-2">
                      <strong>Current Image:</strong>
                    </div>
                    <img
                      src={getImageUrl(form.liveImage)}
                      alt="Current Normal Match Banner"
                      style={{ 
                        width: 120, 
                        height: 80, 
                        marginBottom: 8, 
                        borderRadius: 4, 
                        border: '1px solid #eee', 
                        objectFit: 'cover',
                        display: 'block'
                      }}
                      onError={(e) => {
                        console.error('Live image failed to load:', form.liveImage);
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                      onLoad={() => {
                        console.log('Live image loaded successfully:', form.liveImage);
                      }}
                    />
                    <div className="mb-2">
                      <small className="text-muted">To replace the current image, select a new file below:</small>
                    </div>
                  </div>
                ) : (
                  <div className="mb-2">
                    <small className="text-muted">No image currently selected</small>
                  </div>
                )}
                <Form.Control type="file" accept="image/*" onChange={handleLiveImageChange} disabled={liveImageUploading} />
                {liveImageUploading && <div className="mt-2">Uploading...</div>}
                {liveImageError && <div className="text-danger mt-2">{liveImageError}</div>}
                {(errors.liveImage && formTouched.liveImage) && <div className="text-danger">{errors.liveImage}</div>}
                {fieldErrors.liveImage && <div className="text-danger">{fieldErrors.liveImage}</div>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Highlights Image<span style={{ color: 'red' }}>*</span></Form.Label>
                {form.highlightsImage ? (
                  <div>
                    <div className="mb-2">
                      <strong>Current Image:</strong>
                    </div>
                    <img
                      src={getImageUrl(form.highlightsImage)}
                      alt="Current Highlights Image"
                      style={{ 
                        width: 120, 
                        height: 80, 
                        marginBottom: 8, 
                        borderRadius: 4, 
                        border: '1px solid #eee', 
                        objectFit: 'cover',
                        display: 'block'
                      }}
                      onError={(e) => {
                        console.error('Highlights image failed to load:', form.highlightsImage);
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                      onLoad={() => {
                        console.log('Highlights image loaded successfully:', form.highlightsImage);
                      }}
                    />
                    <div className="mb-2">
                      <small className="text-muted">To replace the current image, select a new file below:</small>
                    </div>
                  </div>
                ) : (
                  <div className="mb-2">
                    <small className="text-muted">No image currently selected</small>
                  </div>
                )}
                <Form.Control type="file" accept="image/*" onChange={handleHighlightsImageChange} disabled={highlightsImageUploading} />
                {highlightsImageUploading && <div className="mt-2">Uploading...</div>}
                {highlightsImageError && <div className="text-danger mt-2">{highlightsImageError}</div>}
                {(errors.highlightsImage && formTouched.highlightsImage) && <div className="text-danger">{errors.highlightsImage}</div>}
                {fieldErrors.highlightsImage && <div className="text-danger">{fieldErrors.highlightsImage}</div>}
              </Form.Group>
              {/* Add more fields as needed */}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={!isFormValid || saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </Modal.Footer>
        </Modal>
        <DeleteModal
          show={showDeleteModal}
          handleClose={closeDeleteModal}
          deleteModalFunction={confirmDelete}
          title="Delete Age Category"
          message={`Are you sure you want to delete '${deleteIndex !== null ? ageCategories[deleteIndex].ageCategory : ''}'?`}
        />
      </div>
    </div>
  );
});

export default AgeCategoryTable; 
